import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * "Masz niezapisane zmiany" (owner 2026-10-23 13:34: „przed zamknięciem okna pytaj, czy zapisać zmiany, jeśli były”).
 *
 * A form says whether it has unsaved changes and how to save or drop them (`useUnsavedGuard`). Whatever closes it from
 * outside — the card's "Zamknij", Escape, another record, a link to another screen — goes through `leaveGuarded`:
 * nothing changed, it goes at once; else one centred choice (`UnsavedChangesHost`, in the app's layout): "Zapisz"
 * (saves, then goes; a failed save stays and the form shows why), "Nie zapisuj" (drops and goes), "Anuluj" (stays;
 * Escape too). A choice, not a „czy na pewno”. The form's own "Anuluj" is already "nie zapisuj" (`leaveWithoutAsking`).
 * Closing or reloading the browser tab: the browser's own question (`beforeunload`), only while something is changed.
 *
 * Owner 2026-10-23 21:14 („jak wychodzę z jakiejkolwiek edycji to zapytaj o zapisanie przed wyjściem — katalogi,
 * kosztorysy, sprzętówki, wszystko … jeśli nie to nie”): every dialog's Escape asks (`DialogLayer`), so do the card's
 * tabs and the browser's "Wstecz"; a sheet row that saves when the focus leaves it tells its save under way
 * (`pending`): leaving waits for it and asks only when it did not save.
 */
export type UnsavedGuard = {
  dirty: boolean;
  /**
   * Saves; false (or a throw) when it did not — the form stays and shows why (409, 422, a wrong field). Gets the
   * form's element (where its ref is), e.g. for `validForm`.
   */
  save: (el: Element | null) => Promise<boolean>;
  /** "Nie zapisuj": what to forget besides closing (e.g. the P13 draft). */
  discard?: (() => void) | undefined;
  /**
   * A save already under way (a sheet row saving on blur — the click that leaves blurs it first): leaving waits for it,
   * then asks only if the form is still changed (the save failed).
   */
  pending?: Promise<unknown> | null | undefined;
};

/** A form's guard and where it is on the page (review I1: a close asks only about the forms inside what it closes). */
type Entry = { guard: UnsavedGuard; el: Element | null };
const guards = new Set<Entry>();
/** The open host's way to ask; null without it (unit tests of a single form): everything goes as before. */
let ask: ((go: () => void, inside: UnsavedGuard[]) => void) | null = null;
/** While > 0 closing does not ask (the form's "Anuluj", or right after the choice). */
let passing = 0;

/**
 * The changed forms: inside `scope` (a dialog, the event panel), or anywhere for `undefined` (leaving the screen,
 * another record). A form not yet on the page counts only for leaving the screen.
 */
export const changed = (scope?: Element | null): UnsavedGuard[] =>
  [...guards].filter((e) => e.guard.dirty && (scope === undefined || (!!scope && !!e.el && scope.contains(e.el)))).map((e) => e.guard);

/** The same values, whatever the order of keys and with `undefined` as absent (a form's draft against how it opened). */
export function sameValues(a: unknown, b: unknown): boolean {
  const norm = (v: unknown): unknown =>
    Array.isArray(v) ? v.map(norm) : v !== null && typeof v === 'object' ? Object.fromEntries(Object.keys(v).sort().filter((k) => (v as Record<string, unknown>)[k] !== undefined).map((k) => [k, norm((v as Record<string, unknown>)[k])])) : v;
  return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
}

/**
 * Registers the form while it is shown. Returns the ref for the form's root element, so closing a dialog or a panel
 * asks only about the forms inside it.
 */
export function useUnsavedGuard(guard: UnsavedGuard): (el: Element | null) => void {
  const [entry] = useState<Entry>(() => ({ guard, el: null }));
  entry.guard = { ...guard, save: () => guard.save(entry.el) };
  useLayoutEffect(() => {
    guards.add(entry);
    return () => void guards.delete(entry);
  }, [entry]);
  useEffect(() => {
    if (!guard.dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // older browsers ask only with a value set
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [guard.dirty]);
  return useCallback(
    (el: Element | null) => {
      entry.el = el;
    },
    [entry],
  );
}

/**
 * Close, switch or leave: at once when nothing is changed, else after the choice (never with "Anuluj"). `scope`: what
 * closes (a dialog, the event panel) — only the forms inside it count; left out: the whole screen.
 */
export function leaveGuarded(go: () => void, scope?: Element | null): void {
  const inside = changed(scope);
  if (passing > 0 || !ask || inside.length === 0) return go();
  const under = inside.map((g) => g.pending).filter((p): p is Promise<unknown> => !!p);
  if (under.length === 0) return ask(go, inside);
  // the saves resolve, then React shows their result (a render later): look again only then
  void Promise.allSettled(under)
    .then(() => new Promise((r) => setTimeout(r, 10)))
    .then(() => leaveGuarded(go, scope));
}

/**
 * Owner 2026-10-27 12:41 („przed wyjściem z tego okna powiedz, że zapytania nie zostały wysłane”): a screen with its own
 * question before it is left — not about a form (e.g. „Dostępność → Ludzie”: questions not sent). `leaves(to)`: whether
 * going to `to` (path and query) leaves it; `ask(go)`: shows its question, `go` leaves (never called — stays).
 */
export type LeavePrompt = { leaves: (to: string) => boolean; ask: (go: () => void) => void };
const prompts = new Set<{ prompt: LeavePrompt }>();

/** The prompt that asks before going to `to`, null when none. */
export function promptFor(to: string): LeavePrompt | null {
  for (const e of prompts) if (e.prompt.leaves(to)) return e.prompt;
  return null;
}

/**
 * Registers the screen's question while `active`; then closing or reloading the tab gets the browser's own question
 * too (`beforeunload` — the browser's text, Safari shows no custom one).
 */
export function useLeavePrompt(active: boolean, prompt: LeavePrompt): void {
  const [entry] = useState(() => ({ prompt }));
  entry.prompt = prompt;
  useLayoutEffect(() => {
    if (!active) return;
    prompts.add(entry);
    return () => void prompts.delete(entry);
  }, [active, entry]);
  useEffect(() => {
    if (!active) return;
    const warn = (e: BeforeUnloadEvent) => {
      // review I3: the app's own full-page move (logout, an expired session, „odśwież”) does not ask
      if (leavingApp) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [active]);
}

/** Set while the app itself leaves the page (`leaveApp`). */
let leavingApp = false;

/**
 * Review I3: a full-page move the app makes itself — logout, the redirect after an expired session, „odśwież” of a new
 * version, the error screen's reload: a screen's question (`useLeavePrompt`) does not stop it. The mark stays a moment
 * (the browser may ask `beforeunload` after the call returns), then clears, e.g. when the move was only a download.
 */
export function leaveApp(go: () => void): void {
  leavingApp = true;
  try {
    go();
  } finally {
    setTimeout(() => {
      leavingApp = false;
    }, 1000);
  }
}

/**
 * Leaving the screen for `to`: first the changed forms (`leaveGuarded`), then the screen's own question
 * (`useLeavePrompt`), then `go`.
 */
export function leaveScreen(to: string, go: () => void): void {
  leaveGuarded(() => {
    const prompt = promptFor(to);
    if (!prompt) return go();
    prompt.ask(go);
  });
}

/** The host's way to ask (null when it goes). */
export function setAsk(fn: typeof ask): void {
  ask = fn;
}

/** True while a close goes without asking. */
export const isPassing = (): boolean => passing > 0;

/** True while some form anywhere has unsaved changes (the browser's "Wstecz"). */
export const anyChanged = (): boolean => changed().length > 0;

/** The form's own "Anuluj" (or a step after the choice): go without asking. */
export function leaveWithoutAsking(go: () => void): void {
  passing++;
  try {
    go();
  } finally {
    passing--;
  }
}


/**
 * The values a form opened with (those of its first render), to compare its draft with: `sameValues(now, opening)` —
 * typed back to how it was counts as unchanged. A new `key` (another record, its data loaded) takes them again.
 */
export function useOpening<T>(values: T, key?: unknown): T {
  const at = useRef({ key, values });
  if (at.current.key !== key) at.current = { key, values };
  return at.current.values;
}

/** The browser's own checks of the form around `el` (`required`, `min`…): false, with the field shown, when it fails. */
export function validForm(el: Element | null): boolean {
  const form = el instanceof HTMLFormElement ? el : el?.closest('form');
  return !form || form.reportValidity();
}

/**
 * A sheet row that saves itself (on blur, on Enter): registered like a form, with its save under way told to leaving
 * (`pending`) — the click that leaves blurs the row first, so leaving waits for that save and asks only when it failed.
 * Call the returned `save` from the row (it never runs twice at once); the question's "Zapisz" calls it too.
 */
export function useRowGuard(dirty: boolean, save: () => Promise<boolean>): { ref: (el: Element | null) => void; save: () => Promise<boolean> } {
  const latest = useRef(save);
  latest.current = save;
  const inflight = useRef<Promise<boolean> | null>(null);
  const [pending, setPending] = useState<Promise<boolean> | null>(null);
  const run = useCallback(() => {
    if (inflight.current) return inflight.current;
    const p = latest.current()
      .catch(() => false)
      .finally(() => {
        inflight.current = null;
        setPending(null);
      });
    inflight.current = p;
    setPending(p);
    return p;
  }, []);
  const ref = useUnsavedGuard({ dirty, pending, save: run });
  return { ref, save: run };
}
